import { siteDetailPath } from '@/lib/siteDashboard';
import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { useState } from 'react';
import { TimerOff, CheckCircle2, ChevronRight } from 'lucide-react';
import type { StartTimeoutRange } from '@evi/shared';
import { useStartTimeouts, formatTimeoutSummary, lastChargeLabel, siblingCableLabel } from '@/hooks/useStartTimeouts';
import { startTimeoutEmptyMessage } from '@/lib/startTimeoutPresentation';
import styles from '../DashboardPage.module.css';

/**
 * Two-Minute Window 4.5 — start timeouts widget.
 *
 * A driver taps Start in the app, the charger accepts, and nobody
 * plugs in before the charger's plug-in window (120 s on the
 * StarCharge Artemis fleet) deauthorises it. Until this widget those
 * starts were indistinguishable from a driver changing their mind and
 * read as soft demand. This lists the SITES and CHARGERS over the
 * threshold — above 40% timeouts over ten or more accepted starts —
 * flagged sites first (the site is what the host answers for; one
 * stall at a three-stall site rarely reaches ten starts in 30 days,
 * the site does), then flagged chargers, with the number the host
 * needs to see: "9 of 12 starts · 75%" and "no completed charge in
 * N days".
 *
 * Read-only. Starts the charger did not accept (refused / offline) are not in
 * the rate but are shown in the row so a site that refuses starts is
 * not mistaken for one with no demand.
 */
export default function StartTimeoutsWidget({ onNavigate, chargerCount }: { onNavigate?: (to: string) => void; chargerCount?: number }) {
  const isSite = useSiteDashboard();
  const [range, setRange] = useState<StartTimeoutRange>('30d');
  const q = useStartTimeouts(range);
  const data = q.data;
  const flaggedSites = data?.locations.filter((r) => r.flagged) ?? [];
  const flaggedChargers = data?.chargers.filter((r) => r.flagged) ?? [];
  const total = flaggedSites.length + flaggedChargers.length;

  const go = (to: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(to);
    }
  };

  return (
    <div className={styles.chartBody}>
      <div className={styles.chartHeader}>
        <div className={styles.chartIcon}><TimerOff size={14} /></div>
        <p className={styles.chartTitle}>Start timeouts</p>
        {/* Badge + range toggle share the header's right edge: one
            auto-margin wrapper, the children's own auto margins zeroed
            (both .anomCountBadge and .topRevToggle set margin-left:auto). */}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {total > 0 && <span className={styles.anomCountBadge} style={{ marginLeft: 0 }}>{total}</span>}
        <div className={styles.topRevToggle} role="tablist" style={{ marginLeft: 0 }}>
          {(['7d', '30d', '90d'] as const).map((r) => (
            <button
              key={r}
              type="button"
              role="tab"
              aria-selected={range === r}
              className={`${styles.topRevToggleBtn} ${range === r ? styles.topRevToggleBtnActive : ''}`}
              onClick={() => setRange(r)}
            >
              {r}
            </button>
          ))}
        </div>
        </span>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && total === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><CheckCircle2 size={18} /></span>
          <p>
            {startTimeoutEmptyMessage(data, range, chargerCount)}
          </p>
        </div>
      )}

      {data && total > 0 && (
        <div className={styles.anomList}>
          {flaggedSites.map((row) => {
            return (
              <a
                key={`site-${row.location_id}`}
                href={(isSite ? siteDetailPath(row.location_id) : `/locations/${row.location_id}`)}
                onClick={go((isSite ? siteDetailPath(row.location_id) : `/locations/${row.location_id}`))}
                className={styles.anomRow}
                title={[`${formatTimeoutSummary(row)} timed out`, siblingCableLabel(row), row.rejected > 0 ? `${row.rejected} not accepted` : '', lastChargeLabel(row.last_charged_at)].filter(Boolean).join(' · ')}
              >
                <span className={`${styles.anomDot} ${styles.anomDotCritical}`} />
                <div className={styles.anomBody}>
                  <span className={styles.anomTitleLine}>
                    {row.location_name} · site · {row.charger_count} charger{row.charger_count === 1 ? '' : 's'}
                    {row.offline_charger_count > 0 ? ` · ${row.offline_charger_count} offline` : ''}
                  </span>
                  <span className={styles.anomDescLine}>
                    {formatTimeoutSummary(row)} timed out
                    {row.rejected > 0 ? ` · ${row.rejected} not accepted` : ''}
                  </span>
                  <span className={styles.anomDescLine}>
                    {row.sibling_cable > 0 ? `${siblingCableLabel(row)} · ` : ''}
                    {lastChargeLabel(row.last_charged_at)}
                  </span>
                </div>
                <ChevronRight size={14} className={styles.topRevChevron} />
              </a>
            );
          })}
          {flaggedChargers.map((row) => {
            return (
              <a
                key={`charger-${row.charger_id}`}
                href={(isSite ? siteDetailPath(row.location_id) : `/chargers/${row.charger_id}`)}
                onClick={go((isSite ? siteDetailPath(row.location_id) : `/chargers/${row.charger_id}`))}
                className={styles.anomRow}
                title={[`${formatTimeoutSummary(row)} timed out`, siblingCableLabel(row), row.rejected > 0 ? `${row.rejected} not accepted` : '', lastChargeLabel(row.last_charged_at)].filter(Boolean).join(' · ')}
              >
                <span className={`${styles.anomDot} ${styles.anomDotWarning}`} />
                <div className={styles.anomBody}>
                  <span className={styles.anomTitleLine}>
                    {row.charger_identity}
                    {row.location_name ? ` · ${row.location_name}` : ''}
                    {!row.online ? ' · offline' : ''}
                  </span>
                  <span className={styles.anomDescLine}>
                    {formatTimeoutSummary(row)} timed out
                    {row.rejected > 0 ? ` · ${row.rejected} not accepted` : ''}
                  </span>
                  <span className={styles.anomDescLine}>
                    {row.sibling_cable > 0 ? `${siblingCableLabel(row)} · ` : ''}
                    {lastChargeLabel(row.last_charged_at)}
                  </span>
                </div>
                <ChevronRight size={14} className={styles.topRevChevron} />
              </a>
            );
          })}
        </div>
      )}

      {data && (
        <p className={styles.chartDesc} style={{ maxWidth: 'none', paddingTop: 8 }}>
          Flag: above {Math.round(data.threshold.rate * 100)}% of {data.threshold.min_starts}+ accepted starts.
          A start times out when the charger accepts it and nobody plugs in within {data.threshold.window_seconds}s.
          "Cable in another pedestal" = a neighbouring charger at the site saw a cable during that window.
          {data.sibling_history_from === null ? ' No charger status history on v2 yet. The cable signal reads 0 by absence of data.' : ''}
          {data.session_basis === 'owned_chargers' ? " Fleet view: every app start on your fleet's chargers, whoever paid." : ''}
        </p>
      )}
    </div>
  );
}
